import { useRef, useCallback, useEffect } from 'react'
import { ResizeObserver } from '@juggle/resize-observer'
import Core from 'handsontable/core'

interface IHookArgs {
  table: Core
}

/**
 * Update the TD and TH heights for rows that have Lists
 * and use the Chips MUI component.
 * - This hook is currently used for Lists.
 * @param {Object} args Hook arguments
 * @param {Handsontable} args.table Handsontable table reference
 */
export const useRowHeightObserver = ({ table }: IHookArgs) => {
  const tableRef = useRef(table) // Use a copy of table since the instance can be lost.

  /*
   * Once the row-height changes due to the observer, re-render the table to have any
   * selection on that row update to the correct height.
   */
  const renderTableCallback = useCallback(() => {
    try {
      tableRef.current?.render()
    } catch (_error) {
      // Do nothing.
    } // Render method cannot be called on a destroyed Handsontable instance.
  }, [tableRef.current])

  const observer = new ResizeObserver(entries => {
    // Fixes "ResizeObserver loop limit exceeded" error in Sentry.
    window.requestAnimationFrame(() => {
      entries.forEach(entry => {
        if (!entry) return

        const elem: HTMLElement = entry.target.querySelector('.list-chips-items')!

        if (!elem) return

        const PADDING = 10
        const dataAttrName = 'data-test-row-header-idx'
        const height = elem.offsetHeight // Get the chip items container height.

        const siblings = entry.target?.parentNode?.childNodes
        if (!siblings) return

        const TH = Array.from(siblings).find(n => n.nodeName === 'TH') as HTMLElement
        if (!TH) return

        const idx = TH.getAttribute(dataAttrName)
        const clonedLeftColumn = document.querySelector('.ht_clone_left.handsontable')
        if (!clonedLeftColumn) return

        // There's a cloned column number row that needs to be updated, not just the
        // TH in the current row.
        const clonedColumnNumber: HTMLElement = clonedLeftColumn.querySelector(
          `[${dataAttrName}="${idx}"]`,
        )!
        if (!clonedColumnNumber) return

        // HACK: Set column number height for the cloned row, along with the TH.
        TH.style.height = `${height + PADDING}px`
        clonedColumnNumber.style.height = `${height + PADDING}px`
      })

      renderTableCallback()
    })
  })

  /**
   * Refresh the list of items to observe; ideal for every cell update for lists.
   */
  function setRowHeightObserver() {
    const elements = document.querySelectorAll('.list-chips .list-chips-items')

    elements.forEach(elem => {
      const parentTD = elem.closest('td')
      if (parentTD) {
        observer.observe(parentTD as HTMLElement)
      }
    })
  }

  // Run on each render of the parent component to ensure UI update.
  useEffect(() => {
    setRowHeightObserver()
  })

  // Update the table instance reference.
  useEffect(() => {
    tableRef.current = table
  }, [table])

  return {
    setRowHeightObserver,
  }
}
